import adminApiClient from '@/admin/api/adminApiClient'
import {
  ADMIN_STATISTICS_SORT_BY_VALUES,
  SORT_DIRECTION_VALUES,
  buildAdminStreamOwnerUpdateRequest,
  buildAdminStreamUpdateRequest,
  buildAdminAiProfileUpsertRequest,
  buildAdminSchemaUpsertRequest,
  buildAdminUserPasswordResetRequest,
  buildAdminUserRolesUpdateRequest,
  buildAdminUserStatusUpdateRequest,
  CONTENT_STATUS_VALUES,
  buildPropertiesUpsertRequest,
  buildCreateInviteRequest,
  buildAdminRssChannelUpsertRequest,
  buildAdminRssCleanerUpsertRequest,
  buildStreamSettingUpsertRequest,
  normalizeAdminContentDto,
  normalizeAdminStatisticsDto,
  normalizeAdminStreamDto,
  normalizeAdminRssChannelDto,
  normalizeAdminRssCleanerDto,
  normalizeContentFormResponse,
  normalizeAdminAiRequestProfileDto,
  normalizeAdminSchemaEntryDto,
  normalizeAdminUserDto,
  normalizeAdminInviteDto,
  normalizePropertiesDto,
  normalizeStreamSettingDefinitionDto,
  normalizeAiPricingSnapshotDto,
  normalizeAiModelPriceDto,
  buildAiPricingSnapshotRequest,
  buildAiModelPriceCreateRequest,
  buildAiModelPriceUpdateRequest,
} from '@/admin/types/adminContracts'
import { unwrapListResponse, unwrapPageResponse } from '@/admin/utils/adminApiUtils'

const encode = value => encodeURIComponent(String(value))

async function withResourceIdFallback(primaryRequest, fallbackRequest) {
  try {
    return await primaryRequest()
  } catch (error) {
    const status = Number(error?.response?.status || 0)
    if (![404, 405].includes(status)) throw error
    return fallbackRequest()
  }
}

export async function getAdminUsers() {
  const { data } = await adminApiClient.get('/api/admin/users')
  return unwrapListResponse(data).map(normalizeAdminUserDto)
}

export async function getAdminUserById(userId) {
  const { data } = await adminApiClient.get(`/api/admin/users/${encode(userId)}`)
  return normalizeAdminUserDto(data)
}

export async function updateAdminUserStatus(userId, payload) {
  const request = buildAdminUserStatusUpdateRequest(payload)
  const { data } = await adminApiClient.put(`/api/admin/users/${encode(userId)}/status`, request)
  return normalizeAdminUserDto(data)
}

export async function updateAdminUserRoles(userId, payload) {
  const request = buildAdminUserRolesUpdateRequest(payload)
  const { data } = await adminApiClient.put(`/api/admin/users/${encode(userId)}/roles`, request)
  return normalizeAdminUserDto(data)
}

export async function resetAdminUserPassword(userId, payload) {
  const request = buildAdminUserPasswordResetRequest(payload)
  const { data } = await adminApiClient.post(`/api/admin/users/${encode(userId)}/reset-password`, request)
  return data
}

export async function getAdminAiProfiles({ includeInactive = false } = {}) {
  const { data } = await adminApiClient.get('/api/admin/ai-profiles', {
    params: { includeInactive: Boolean(includeInactive) },
  })
  return unwrapListResponse(data).map(normalizeAdminAiRequestProfileDto)
}

export async function getAdminAiProfileById(profileId) {
  const { data } = await adminApiClient.get(`/api/admin/ai-profiles/${encode(profileId)}`)
  return normalizeAdminAiRequestProfileDto(data)
}

export async function getAdminAiProfileByKey(profileKey) {
  const { data } = await adminApiClient.get(`/api/admin/ai-profiles/by-key/${encode(profileKey)}`)
  return normalizeAdminAiRequestProfileDto(data)
}

export async function createAdminAiProfile(payload) {
  const request = buildAdminAiProfileUpsertRequest(payload)
  const { data } = await adminApiClient.post('/api/admin/ai-profiles', request)
  return normalizeAdminAiRequestProfileDto(data)
}

export async function updateAdminAiProfile(profileId, payload) {
  const request = buildAdminAiProfileUpsertRequest(payload)
  const { data } = await adminApiClient.put(`/api/admin/ai-profiles/${encode(profileId)}`, request)
  return normalizeAdminAiRequestProfileDto(data)
}

export async function deleteAdminAiProfile(profileId) {
  await adminApiClient.delete(`/api/admin/ai-profiles/${encode(profileId)}`)
}

export async function getAdminSchemas({ profileKey = '', param = '' } = {}) {
  const params = {}
  if (profileKey) params.profileKey = profileKey
  if (param) params.param = param
  const { data } = await adminApiClient.get('/api/admin/schemas', { params })
  return unwrapListResponse(data).map(normalizeAdminSchemaEntryDto)
}

export async function upsertAdminSchema(payload) {
  const request = buildAdminSchemaUpsertRequest(payload)
  const { data } = await adminApiClient.post('/api/admin/schemas', request)
  return normalizeAdminSchemaEntryDto(data)
}

export async function deleteAdminSchema({ profileKey, param, streamType = null }) {
  const params = {
    profileKey,
    param,
  }
  if (streamType) params.streamType = streamType
  await adminApiClient.delete('/api/admin/schemas', { params })
}

export async function getAdminInvites() {
  const { data } = await adminApiClient.get('/api/admin/invites')
  return unwrapListResponse(data).map(normalizeAdminInviteDto)
}

export async function createAdminInvites(payload) {
  const request = buildCreateInviteRequest(payload)
  const { data } = await adminApiClient.post('/api/admin/invites', request)
  return unwrapListResponse(data).map(normalizeAdminInviteDto)
}

export async function revokeAdminInvite(inviteId) {
  const { data } = await adminApiClient.post(`/api/admin/invites/${encode(inviteId)}/revoke`)
  return normalizeAdminInviteDto(data)
}

export async function getAdminProperties() {
  const { data } = await adminApiClient.get('/api/admin/properties')
  return unwrapListResponse(data).map(normalizePropertiesDto)
}

export async function createAdminProperty(payload) {
  const request = buildPropertiesUpsertRequest(payload)
  const { data } = await adminApiClient.post('/api/admin/properties', request)
  return normalizePropertiesDto(data)
}

export async function updateAdminProperty(propertyId, payload) {
  const request = buildPropertiesUpsertRequest(payload)
  const response = await withResourceIdFallback(
    () => adminApiClient.put(`/api/admin/properties/${encode(propertyId)}`, request),
    () => adminApiClient.put('/api/admin/properties', { ...request, id: Number(propertyId) || propertyId })
  )
  return normalizePropertiesDto(response.data)
}

export async function deleteAdminProperty(propertyId) {
  await withResourceIdFallback(
    () => adminApiClient.delete(`/api/admin/properties/${encode(propertyId)}`),
    () => adminApiClient.delete('/api/admin/properties', { params: { id: Number(propertyId) || propertyId } })
  )
}

export async function getAdminStreamSettings() {
  const { data } = await adminApiClient.get('/api/admin/stream-settings')
  return unwrapListResponse(data).map(normalizeStreamSettingDefinitionDto)
}

export async function getAdminStreamSettingsByType({ streamType, aiName = '' }) {
  const params = { streamType }
  if (aiName) params.aiName = aiName
  const { data } = await adminApiClient.get('/api/admin/stream-settings/by-type', { params })
  return unwrapListResponse(data).map(normalizeStreamSettingDefinitionDto)
}

export async function createAdminStreamSetting(payload) {
  const request = buildStreamSettingUpsertRequest(payload)
  const { data } = await adminApiClient.post('/api/admin/stream-settings', request)
  return normalizeStreamSettingDefinitionDto(data)
}

export async function updateAdminStreamSetting(settingId, payload) {
  const request = buildStreamSettingUpsertRequest(payload)
  const response = await withResourceIdFallback(
    () => adminApiClient.put(`/api/admin/stream-settings/${encode(settingId)}`, request),
    () => adminApiClient.put('/api/admin/stream-settings', { ...request, id: Number(settingId) || settingId })
  )
  return normalizeStreamSettingDefinitionDto(response.data)
}

export async function deleteAdminStreamSetting(settingId) {
  await withResourceIdFallback(
    () => adminApiClient.delete(`/api/admin/stream-settings/${encode(settingId)}`),
    () => adminApiClient.delete('/api/admin/stream-settings', { params: { id: Number(settingId) || settingId } })
  )
}

export async function getAdminRssChannels({ category = '', disabled = null } = {}) {
  const params = {}
  if (String(category || '').trim()) params.category = String(category).trim()
  if (typeof disabled === 'boolean') params.disabled = disabled
  const { data } = await adminApiClient.get('/api/admin/rss-channels', { params })
  return unwrapListResponse(data).map(normalizeAdminRssChannelDto)
}

export async function getRssChannelCategories() {
  const { data } = await adminApiClient.get('/api/rss-channels/categories')
  const list = Array.isArray(data) ? data : unwrapListResponse(data)
  return [...new Set(list.map(item => String(item == null ? '' : item).trim()).filter(Boolean))]
}

export async function getAdminRssChannelById(channelId) {
  const { data } = await adminApiClient.get(`/api/admin/rss-channels/${encode(channelId)}`)
  return normalizeAdminRssChannelDto(data)
}

export async function createAdminRssChannel(payload) {
  const request = buildAdminRssChannelUpsertRequest(payload)
  const { data } = await adminApiClient.post('/api/admin/rss-channels', request)
  return normalizeAdminRssChannelDto(data)
}

export async function updateAdminRssChannel(channelId, payload) {
  const request = buildAdminRssChannelUpsertRequest(payload, { partial: true })
  const { data } = await adminApiClient.put(`/api/admin/rss-channels/${encode(channelId)}`, request)
  return normalizeAdminRssChannelDto(data)
}

export async function deleteAdminRssChannel(channelId) {
  await adminApiClient.delete(`/api/admin/rss-channels/${encode(channelId)}`)
}

export async function getAdminRssCleaners() {
  const { data } = await adminApiClient.get('/api/admin/rss-cleaners')
  return unwrapListResponse(data).map(normalizeAdminRssCleanerDto)
}

export async function getAdminRssCleanerById(cleanerId) {
  const { data } = await adminApiClient.get(`/api/admin/rss-cleaners/${encode(cleanerId)}`)
  return normalizeAdminRssCleanerDto(data)
}

export async function createAdminRssCleaner(payload) {
  const request = buildAdminRssCleanerUpsertRequest(payload)
  const { data } = await adminApiClient.post('/api/admin/rss-cleaners', request)
  return normalizeAdminRssCleanerDto(data)
}

export async function updateAdminRssCleaner(cleanerId, payload) {
  const request = buildAdminRssCleanerUpsertRequest(payload, { partial: true })
  const { data } = await adminApiClient.put(`/api/admin/rss-cleaners/${encode(cleanerId)}`, request)
  return normalizeAdminRssCleanerDto(data)
}

export async function deleteAdminRssCleaner(cleanerId) {
  await adminApiClient.delete(`/api/admin/rss-cleaners/${encode(cleanerId)}`)
}

export async function getAdminStreams({
  userId = null,
  username = '',
  type = '',
  page = 0,
  size = 20,
  sortBy = '',
  sortDir = 'desc',
} = {}) {
  const params = {}
  if (Number(userId) > 0) params.userId = Number(userId)
  if (String(username || '').trim()) params.username = String(username).trim()
  if (String(type || '').trim()) params.type = String(type).trim()
  params.page = Math.max(0, Number(page) || 0)
  params.size = Math.max(1, Number(size) || 20)
  if (String(sortBy || '').trim()) params.sortBy = String(sortBy).trim()
  if (['asc', 'desc'].includes(String(sortDir || '').toLowerCase())) {
    params.sortDir = String(sortDir).toLowerCase()
  }

  const { data } = await adminApiClient.get('/api/admin/streams', { params })
  const pageResponse = unwrapPageResponse(data, params.page, params.size)

  return {
    content: pageResponse.content.map(normalizeAdminStreamDto),
    totalElements: pageResponse.totalElements,
    totalPages: pageResponse.totalPages,
    page: pageResponse.page,
    size: pageResponse.size,
  }
}

export async function getAdminStreamById(streamId) {
  const { data } = await adminApiClient.get(`/api/admin/streams/${encode(streamId)}`)
  return normalizeAdminStreamDto(data)
}

export async function updateAdminStream(streamId, payload) {
  const request = buildAdminStreamUpdateRequest(payload)
  const { data } = await adminApiClient.put(`/api/admin/streams/${encode(streamId)}`, request)
  return normalizeAdminStreamDto(data)
}

export async function updateAdminStreamOwner(streamId, payload) {
  const request = buildAdminStreamOwnerUpdateRequest(payload)
  const { data } = await adminApiClient.put(`/api/admin/streams/${encode(streamId)}/owner`, request)
  return normalizeAdminStreamDto(data)
}

export async function deleteAdminStream(streamId) {
  await adminApiClient.delete(`/api/admin/streams/${encode(streamId)}`)
}

export async function getAdminStatistics({
  userId = null,
  fromDate = '',
  toDate = '',
  sortBy = 'userId',
  sortDir = 'desc',
} = {}) {
  const params = {}
  if (Number(userId) > 0) params.userId = Number(userId)
  if (String(fromDate || '').trim()) params.fromDate = String(fromDate).trim()
  if (String(toDate || '').trim()) params.toDate = String(toDate).trim()
  if (ADMIN_STATISTICS_SORT_BY_VALUES.includes(String(sortBy || '').trim())) {
    params.sortBy = String(sortBy).trim()
  }
  if (SORT_DIRECTION_VALUES.includes(String(sortDir || '').toLowerCase())) {
    params.sortDir = String(sortDir).toLowerCase()
  }

  const { data } = await adminApiClient.get('/api/admin/statistics', { params })
  return normalizeAdminStatisticsDto(data)
}

export async function getAdminStreamContents(
  streamId,
  { page = 0, size = 20, sortBy = 'updatedAt', sortDir = 'desc', statuses = [] } = {}
) {
  const params = new URLSearchParams()
  params.append('page', String(Math.max(0, Number(page) || 0)))
  params.append('size', String(Math.max(1, Number(size) || 20)))
  if (String(sortBy || '').trim()) params.append('sortBy', String(sortBy).trim())
  if (['asc', 'desc'].includes(String(sortDir || '').toLowerCase())) {
    params.append('sortDir', String(sortDir).toLowerCase())
  }

  const normalizedStatuses = Array.isArray(statuses)
    ? statuses.filter(status => CONTENT_STATUS_VALUES.includes(String(status)))
    : []
  normalizedStatuses.forEach(status => {
    params.append('statuses', String(status))
  })

  const { data } = await adminApiClient.get(`/api/admin/streams/${encode(streamId)}/contents`, { params })
  const pageResponse = unwrapPageResponse(data, Number(page) || 0, Number(size) || 20)

  return {
    content: pageResponse.content.map(normalizeAdminContentDto),
    totalElements: pageResponse.totalElements,
    totalPages: pageResponse.totalPages,
    page: pageResponse.page,
    size: pageResponse.size,
  }
}

export async function getAdminStreamContentById(streamId, contentId) {
  const { data } = await adminApiClient.get(`/api/admin/streams/${encode(streamId)}/contents/${encode(contentId)}`)
  return normalizeAdminContentDto(data)
}

export async function getStreamContentFormByType(streamType) {
  const { data } = await adminApiClient.get(`/api/streams/${encode(streamType)}/content-form`)
  return normalizeContentFormResponse(data)
}

// ---------- AI pricing ----------

export async function getAiPricingSnapshots() {
  const { data } = await adminApiClient.get('/api/admin/ai-pricing/snapshots')
  return unwrapListResponse(data).map(normalizeAiPricingSnapshotDto)
}

export async function getAiPricingSnapshot(snapshotId) {
  const { data } = await adminApiClient.get(`/api/admin/ai-pricing/snapshots/${encode(snapshotId)}`)
  return normalizeAiPricingSnapshotDto(data)
}

export async function createAiPricingSnapshot(payload) {
  const request = buildAiPricingSnapshotRequest(payload)
  const { data } = await adminApiClient.post('/api/admin/ai-pricing/snapshots', request)
  return normalizeAiPricingSnapshotDto(data)
}

export async function updateAiPricingSnapshot(snapshotId, payload) {
  const request = buildAiPricingSnapshotRequest(payload)
  const { data } = await adminApiClient.put(`/api/admin/ai-pricing/snapshots/${encode(snapshotId)}`, request)
  return normalizeAiPricingSnapshotDto(data)
}

export async function deleteAiPricingSnapshot(snapshotId) {
  await adminApiClient.delete(`/api/admin/ai-pricing/snapshots/${encode(snapshotId)}`)
}

export async function publishAiPricingSnapshot(snapshotId) {
  const { data } = await adminApiClient.post(`/api/admin/ai-pricing/snapshots/${encode(snapshotId)}/publish`)
  return normalizeAiPricingSnapshotDto(data)
}

export async function archiveAiPricingSnapshot(snapshotId) {
  const { data } = await adminApiClient.post(`/api/admin/ai-pricing/snapshots/${encode(snapshotId)}/archive`)
  return normalizeAiPricingSnapshotDto(data)
}

export async function addAiModelPrice(snapshotId, payload) {
  const request = buildAiModelPriceCreateRequest(payload)
  const { data } = await adminApiClient.post(`/api/admin/ai-pricing/snapshots/${encode(snapshotId)}/models`, request)
  return normalizeAiModelPriceDto(data)
}

export async function updateAiModelPrice(snapshotId, modelPriceId, payload) {
  const request = buildAiModelPriceUpdateRequest(payload)
  const { data } = await adminApiClient.put(
    `/api/admin/ai-pricing/snapshots/${encode(snapshotId)}/models/${encode(modelPriceId)}`,
    request
  )
  return normalizeAiModelPriceDto(data)
}

export async function deleteAiModelPrice(snapshotId, modelPriceId) {
  await adminApiClient.delete(
    `/api/admin/ai-pricing/snapshots/${encode(snapshotId)}/models/${encode(modelPriceId)}`
  )
}
